import React from 'react';
import { Field, Fields, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { Form, Header } from 'semantic-ui-react';
import { renderInput, renderDropbox, renderDate, renderPhone } from '../FormFields';
import { states } from '../../utils/states';
import { getGenders } from '../../utils/dates';
import { formatDate, formatPhone, scrollOnSubmitError } from '../../utils/utils';
import { validateEnroll, maxLength, validateExp } from '../../utils/validation';
import _ from 'lodash';
import ExternalModal from '../Modals/ExternalModal';

const hcpValidation = (values) => {
  const errors = validateEnroll(values, true);
  return errors;
};

const HPSInformation = props => {
  const { handleSubmit, onSubmit, submitFailed, invalid } = props;
  const sanitizeForm = (values) => {
    const finalForm = _.pick(values, ['firstName', 'lastName', 'address1', 'address2', 'city', 'state', 'zip', 'email', 'gender', 'patientMedication', 'pharmacyInsurance.pbmName', 'pharmacyInsurance.memberId', 'pharmacyInsurance.bin', 'pharmacyInsurance.group', 'pharmacyInsurance.pcn', 'prescriber.firstName', 'prescriber.lastName', 'prescriber.npi', 'prescriber.practiceName', 'prescriber.address1', 'prescriber.address2', 'prescriber.city', 'prescriber.state', 'prescriber.zip']);
    finalForm.mobilePhoneNumber = formatPhone(values.areaCode, values.firstThreeDigits, values.lastFourDigits);
    finalForm.dob = formatDate(values.month, values.day, values.year);
    finalForm.hasMarketingOptIn = false;
    finalForm.hasPermissionOptIn = false;
    onSubmit({ values: finalForm, path: `/hps/${props.formType}/finish` });
  };

  const submitBtnHandler = () => {
    scrollOnSubmitError(invalid);
  };

  return (
    <div className="patient-information form">
      <p className="required">Required field.</p>
      <form className="ui form" onSubmit={ handleSubmit(sanitizeForm) }>

        <Header as="h2" className="opt-in-header">
          Patient Information
        </Header>

        <Field
          required
          name="firstName"
          type="text"
          component={ renderInput }
          label="Patient’s First Name"
          errorMessage="First Name is a required field."
          allowNameCharsOnly={ true }
        />
        <Field
          required
          name="lastName"
          type="text"
          component={ renderInput }
          label="Patient’s Last Name"
          errorMessage="Last Name is a required field."
          allowNameCharsOnly={ true }
        />
        <Field
          required
          label="Patient’s Street Address - Line 1"
          errorMessage="Street Address is a required field."
          type="text"
          name="address1"
          component={ renderInput }
        />
        <Field
          label="Patient’s Street Address - Line 2"
          type="text"
          name="address2"
          component={ renderInput }
        />
        <Field
          required
          label="Patient’s City"
          errorMessage="City is a required field."
          type="text"
          name="city"
          component={ renderInput }
        />
        <Form.Group>
          <Field
            required
            label="Patient’s State"
            errorMessage="State is a required field."
            name="state"
            customClass="states"
            options={ states }
            component={ renderDropbox }
          />
          <Field
            required
            label="Patient’s ZIP Code"
            errorMessage="ZIP Code is a required field."
            invalidErrorMessage="ZIP Code is invalid."
            type="tel"
            name="zip"
            customClass="zip-code"
            min="0"
            component={ renderInput }
            normalize={ (value, previousValue) => {
              value = maxLength(5)(value, previousValue);
              if (!validateExp({ type: 'justNumbers', value })) {
                return previousValue;
              }
              return value;
            } }
          />
        </Form.Group>
        <Fields
          required
          names={ ['month', 'day', 'year'] }
          component={ renderDate }
          errorMessage="Date of Birth is a required field."
          invalidErrorMessage="Date of Birth is invalid."
          label="Patient’s Date of Birth"
        />
        <Field
          label="Patient’s Email Address"
          type="email"
          name="email"
          errorMessage="Email is invalid."
          component={ renderInput }
        />
        <Field
          label="Confirm Patient’s Email Address"
          type="email"
          name="confirmEmail"
          errorMessage="Email entry does not match."
          component={ renderInput }
        />
        <Fields
          required
          names={ ['areaCode', 'firstThreeDigits', 'lastFourDigits'] }
          component={ renderPhone }
          errorMessage="Phone Number is a required field."
          invalidErrorMessage="Phone Number is invalid."
          label="Patient’s Phone Number"
        />
        <Field
          required
          label="Patient’s Gender"
          errorMessage="Gender is a required field."
          name="gender"
          customClass="gender"
          options={ getGenders }
          component={ renderDropbox }
        />

        <Header as="h2" className="opt-in-header">
          Patient Prescription Insurance Information
        </Header>

        <Field
          required
          name="pharmacyInsurance.pbmName"
          type="text"
          component={ renderInput }
          label="Insurance Name"
          errorMessage="Insurance Name is a required field."
        />

        <Field
          required
          name="pharmacyInsurance.memberId"
          type="text"
          component={ renderInput }
          label="Rx Member ID Number"
          errorMessage="Rx Member ID Number is a required field."
          alphaNumericOnly={ true }
        />

        <Field
          required
          name="pharmacyInsurance.bin"
          type="text"
          component={ renderInput }
          label="Rx BIN Number"
          errorMessage="Rx Bin Number is a required field."
          invalidErrorMessage="Rx Bin Number is invalid."
          normalize={ (value, previousValue) => {
            value = maxLength(6)(value, previousValue);
            if (!validateExp({ type: 'justNumbers', value })) {
              return previousValue;
            }
            return value;
          } }
        />

        <Field
          name="pharmacyInsurance.group"
          type="text"
          component={ renderInput }
          label="Rx Group Number"
        />

        <Field
          name="pharmacyInsurance.pcn"
          type="text"
          component={ renderInput }
          label="Rx PCN Number"
          alphaNumericOnly={ true }
        />

        <div className="enroll-note">
          <p>Note: Please input your patient’s primary insurance information in the fields above if no Rx specific insurance is available.</p>
        </div>

        <Header as="h2" className="opt-in-header">
          HCP & Practice Information
        </Header>

        <Field
          required
          name="prescriber.firstName"
          type="text"
          component={ renderInput }
          label="HCP First Name"
          errorMessage="HCP First Name is a required field."
          allowNameCharsOnly={ true }
        />

        <Field
          required
          name="prescriber.lastName"
          type="text"
          component={ renderInput }
          label="HCP Last Name"
          errorMessage="HCP Last Name is a required field."
          allowNameCharsOnly={ true }
        />

        <Field
          required
          name="prescriber.npi"
          type="text"
          component={ renderInput }
          label="NPI Number"
          errorMessage="NPI Number is a required field."
          invalidErrorMessage="NPI Number is invalid."
          normalize={ (value, previousValue) => {
            value = maxLength(10)(value, previousValue);
            if (!validateExp({ type: 'justNumbers', value })) {
              return previousValue;
            }
            return value;
          } }
        />

        <Field
          required
          name="prescriber.practiceName"
          type="text"
          component={ renderInput }
          label="Practice Name"
          errorMessage="Practice Name is a required field."
        />

        <Field
          label="Practice Street Address - Line 1"
          type="text"
          name="prescriber.address1"
          component={ renderInput }
        />
        <Field
          label="Practice Street Address - Line 2"
          type="text"
          name="prescriber.address2"
          component={ renderInput }
        />
        <Field
          label="Practice City"
          type="text"
          name="prescriber.city"
          component={ renderInput }
        />
        <Form.Group>
          <Field
            label="Practice State"
            name="prescriber.state"
            customClass="states"
            options={ states }
            component={ renderDropbox }
          />
          <Field
            label="Practice ZIP Code"
            type="tel"
            name="prescriber.zip"
            customClass="zip-code"
            component={ renderInput }
            min="0"
            errorMessage="ZIP Code is invalid."
            invalidErrorMessage="ZIP Code is invalid."
            normalize={ (value, previousValue) => {
              value = maxLength(5)(value, previousValue);
              if (!validateExp({ type: 'justNumbers', value })) {
                return previousValue;
              }
              return value;
            } }
          />
        </Form.Group>

        <p>By providing the information above and clicking submit, your patient certifies that they are at least 18 years old and understand that Takeda and its business partners will use their personal information to enroll them in the Takeda Oncology Co-Pay Assistance Program and provide the support they are asking for.</p>

        <p>Your patient’s privacy is important to us. For more information, please refer to our <ExternalModal url="https://www.takeda.com/privacy-notice/" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">Privacy Notice</a> }/>.</p>

        <p>By submitting this form, your patient agrees to Takeda’s <ExternalModal url="https://www.takeda.com/terms-of-use" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">Terms of Use</a> }/> and <ExternalModal url="https://www.takeda.com/privacy-notice/" trigger={ <a className="track track-2 track-env-global" rel="noopener noreferrer">Privacy Notice</a> }/>.</p>

        <button
          onClick={ submitBtnHandler }
          type="submit"
          className= { `ui button form-button track ${props.submitTrackId} track-env-${props.environment}` }
          disabled={ submitFailed && invalid }
        >
          Submit
        </button>
      </form>
    </div>
  );
};

HPSInformation.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submitTrackId: PropTypes.string.isRequired,
};

export default reduxForm({
  form: 'hpsInformationForm', // a unique identifier for this form
  validate: hcpValidation, // <--- validation function given to redux-form
})(HPSInformation);
